import * as React from 'react'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useDeleteWebDevEndpointMutation,
  useListWebDevEndpointsQuery,
  useRegisterWebDevEndpointMutation,
  type WebDevEndpointSummary
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { StatusPill } from '../../../components/primitives/StatusPill'

/**
 * Settings → Gateways: list/edit Ignition WebDev Basic-auth credentials
 * keyed by gateway URL. Passwords are write-only (proxy secret store).
 */
export function GatewaysTab() {
  const { data: endpoints, isLoading, error, refetch } = useListWebDevEndpointsQuery()
  const [registerEndpoint, registerState] = useRegisterWebDevEndpointMutation()
  const [deleteEndpoint, deleteState] = useDeleteWebDevEndpointMutation()

  const [gatewayUrl, setGatewayUrl] = React.useState('')
  const [username, setUsername] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [editing, setEditing] = React.useState<WebDevEndpointSummary | null>(null)
  const [formError, setFormError] = React.useState('')

  const busy = registerState.isLoading || deleteState.isLoading

  const startEdit = (ep: WebDevEndpointSummary) => {
    setEditing(ep)
    setGatewayUrl(ep.gatewayUrl)
    setUsername(ep.username ?? '')
    setPassword('')
    setFormError('')
  }

  const startCreate = () => {
    setEditing(null)
    setGatewayUrl('')
    setUsername('')
    setPassword('')
    setFormError('')
  }

  const handleSave = async () => {
    const url = gatewayUrl.trim().replace(/\/+$/, '')
    const user = username.trim()
    if (!url) {
      setFormError('Gateway URL is required')
      return
    }
    if (!editing && !user) {
      setFormError('Username is required')
      return
    }
    if (!editing && !password) {
      setFormError('Password is required when adding a gateway')
      return
    }
    if (editing && !user && !password) {
      setFormError('Enter a new username and/or password')
      return
    }
    setFormError('')
    try {
      await registerEndpoint({
        gatewayUrl: url,
        username: user,
        ...(password ? { password } : {})
      })
      toast.success(editing ? 'Gateway credentials updated' : 'Gateway credentials saved')
      setPassword('')
      setEditing(null)
      await refetch()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err))
    }
  }

  const handleDelete = async (ep: WebDevEndpointSummary) => {
    if (!window.confirm(`Remove WebDev credentials for ${ep.gatewayUrl}?`)) return
    try {
      await deleteEndpoint(ep.gatewayUrl)
      toast.success('Gateway credentials removed')
      if (editing?.gatewayUrl === ep.gatewayUrl) startCreate()
      await refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="flex flex-col gap-card">
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col gap-0.5">
              <h3 className="text-sm font-semibold text-ink">Gateway WebDev credentials</h3>
              <p className="text-xs text-ink-3">
                Basic auth the proxy sends on HTTP calls to each Ignition gateway&apos;s{' '}
                <code className="font-mono text-11">/system/webdev/*</code> endpoints. Passwords are
                stored in the proxy secret store and never shown again.
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={startCreate} disabled={busy}>
              <Plus className="size-3.5" />
              Add
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-xs text-ink-3">
              <Loader2 className="size-3.5 animate-spin" />
              Loading gateways…
            </div>
          ) : error ? (
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              {error}
            </div>
          ) : (endpoints?.length ?? 0) === 0 ? (
            <p className="text-xs text-ink-3">
              No gateway credentials yet. Add them here, or enter them during structured ingest.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {(endpoints ?? []).map((ep) => (
                <li
                  key={ep.gatewayUrl}
                  className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => startEdit(ep)}
                  >
                    <div className="truncate font-mono text-xs text-ink">{ep.gatewayUrl}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-11 text-ink-3">
                      <span>{ep.username || '(no username)'}</span>
                      <StatusPill status={ep.hasPassword ? 'run' : 'idle'}>
                        {ep.hasPassword ? 'password set' : 'no password'}
                      </StatusPill>
                    </div>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${ep.gatewayUrl}`}
                    disabled={busy}
                    onClick={() => void handleDelete(ep)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-ink">
            {editing ? 'Edit gateway credentials' : 'Add gateway credentials'}
          </h3>
          <div className="grid gap-1.5">
            <Label htmlFor="gw-url">Gateway URL</Label>
            <Input
              id="gw-url"
              className="font-mono text-xs"
              placeholder="https://gateway.example.com:8043"
              value={gatewayUrl}
              disabled={!!editing || busy}
              onChange={(e) => setGatewayUrl(e.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="gw-user">Username</Label>
              <Input
                id="gw-user"
                className="font-mono text-xs"
                autoComplete="off"
                value={username}
                disabled={busy}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="gw-pass">Password</Label>
              <Input
                id="gw-pass"
                className="font-mono text-xs"
                type="password"
                autoComplete="new-password"
                placeholder={editing?.hasPassword ? 'Leave blank to keep current' : undefined}
                value={password}
                disabled={busy}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
          {formError ? (
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              {formError}
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            {editing ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={startCreate}
              >
                Cancel
              </Button>
            ) : null}
            <Button type="button" size="sm" disabled={busy} onClick={() => void handleSave()}>
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : null}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
